import { SerializedData, Store } from "bernie-core";
import { Logger } from "bernie-logger";
import { PropertyFiltersQuery } from "__generated__/api";
import _SortAndFilter = PropertyFiltersQuery._SortAndFilter;
import _Options = PropertyFiltersQuery._Options;
import { Filter } from "components/flexComponents/PropertyFilters/typings";
import { HotelSearchLinkBuilder } from "components/utility/HotelSearchLinkBuilder/HotelSearchLinkBuilder";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { PROPERTY_FILTERS_COMPONENT_MAP } from "components/shared/PropertyFIlterComponents/map/PropertyFiltersComponentMap";
import { Radio } from "components/shared/PropertyFIlterComponents/components/Radio";
import { action, observable } from "mobx";
import { PropertyFilterPublisher } from "./PropertyFilterPublisher";

const validFilterNames = Object.keys(PROPERTY_FILTERS_COMPONENT_MAP);
/**
 * Class that describes the state of the filters. This will be used as the source of truth.
 *
 * filterName refers to name of the "category" (e.g. Property Class)
 * optionName refers to the individual options for a filter (e.g. 5 stars)
 */
export class PropertyFiltersStore extends Store {
  // e.g. ordered map of key = "propertyClass", value = map of 1 star, 2 star, etc
  private publisher = new PropertyFilterPublisher();

  @observable
  private filtersMap = new Map<string, Filter>();

  @observable
  private originalRegionId: string;

  public constructor(state: SerializedData, logger: Logger) {
    super(state, logger);
  }

  public hydrate(data: SerializedData): void {
    const filtersMap = new Map(
      (data.filtersMap as any[]).map(([k, f]) => {
        return [k, { ...f, options: new Map(f.options) }];
      })
    );

    Object.assign(this, { ...data, filtersMap });
    this.syncFilters();
  }

  public toJSON() {
    /**
     * We are parsing the Map as a Tuple to prevent order changes that happend if we send it as a JSON
     */
    const filters = Array.from(this.filtersMap.keys()).map((filtersMapKey) => {
      const options = Array.from(this.filtersMap.get(filtersMapKey)!.options!).map(([optionKey, option]) => {
        return [optionKey, option];
      });

      return [filtersMapKey, { ...this.filtersMap.get(filtersMapKey), options }];
    });

    return {
      filtersMap: filters,
      originalRegionId: String(this.originalRegionId),
    };
  }

  @action
  public setFilters(filters: _SortAndFilter[]) {
    filters
      .filter((filter) => filter?.options && filter?.label && validFilterNames.includes(filter?.name))
      .map((filter) => {
        const { name, label, options } = filter;

        const optionsMap = new Map<string, _Options>();
        options.forEach((option) => {
          optionsMap.set(option.optionValue || "", option);
        });

        this.filtersMap.set(name, { name, label: label || "", options: optionsMap });
      });
    this.syncFilters();
  }

  @action
  public syncFilters() {
    this.filtersMap.forEach((filter) => {
      filter.options.forEach((option) => {
        this.publisher.subscribe(option.selectedLabel!, (value: boolean) => {
          option.isSelected = value;
        });
      });
    });
  }

  @action
  public setRegion(region: string) {
    this.originalRegionId = String(region);
  }

  public getListOfFilters() {
    return Array.from(this.filtersMap.values());
  }

  public getListOfPopularFilters() {
    const popularFilters = this.filtersMap.get("popularFilter");

    return Array.from(popularFilters?.options.values() || []);
  }

  public getFreeCancellationFilter() {
    const paymentType = this.filtersMap.get("paymentType");
    const freeCancellation = paymentType?.options.get("FREE_CANCELLATION");

    return freeCancellation;
  }

  public isEmpty() {
    return this.filtersMap.size === 0;
  }

  public getNumberOfSelectedFilters() {
    return this.getListOfFilters().reduce((count, filter) => {
      return count + this.getNumberOfSelectedFiltersPerCategory(filter.name);
    }, 0);
  }

  public getNumberOfSelectedFiltersPerCategory(filterName: string) {
    if (filterName === "popularFilter") {
      return 0;
    }

    const filters = this.filtersMap.get(filterName);
    const options = Array.from(filters?.options.values() || []);

    return options.filter((option) => option.isSelected && option.optionValue !== this.originalRegionId).length;
  }

  public isSelected(filterName: string, optionName: string) {
    return this.filtersMap.get(filterName)?.options.get(optionName)?.isSelected;
  }

  @action
  public toggleSelection = (filterName: string, optionName: string) => {
    if (!this.filtersMap.get(filterName)?.options.get(optionName)) return;

    const option = this.filtersMap.get(filterName)!.options.get(optionName)!;
    const value = !option.isSelected;
    const isPopularFilter = filterName === "popularFilter";
    const groupName = isPopularFilter ? option.filterCategory || "" : filterName;

    if (PROPERTY_FILTERS_COMPONENT_MAP[groupName] === Radio) {
      this.clearFilter(groupName);
    }

    this.publisher.next(option.selectedLabel!, value);

    const isRegionId = option.filterCategory === "regionId" || filterName === "regionId";

    if (isRegionId && this.filtersMap.get(filterName)!.options.get(optionName)!.isSelected) {
      return this.filtersMap.get(filterName)!.options.get(optionName)!.optionValue;
    } else {
      return this.originalRegionId;
    }
  };

  @action
  public clearAllFilters() {
    this.getListOfFilters().forEach((filter) => this.clearFilter(filter.name));
  }

  @action
  public clearFilter(filterName: string) {
    if (!this.filtersMap.get(filterName)?.options) {
      return;
    }

    this.filtersMap.get(filterName)!.options.forEach((option) => {
      this.publisher.next(option.selectedLabel!, false);
    });
  }

  public buildLinkToSRP(context?: ExtendedContextStore) {
    const link = new HotelSearchLinkBuilder()
      .withStayLength()
      .withDaysInFuture()
      .withRegionId(this.originalRegionId)
      .withExpandForm();

    // Add filters
    this.getListOfFilters().forEach((filter) => {
      Array.from(filter.options.values())
        .filter((option) => option.isSelected)
        .forEach((option) => {
          // We dont want to get popular filters since they are propagated
          if (option.filterCategory) return;

          link.withFilter(filter.name, option.optionValue || "");
        });
    });

    return link.build(context);
  }
}
